import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ErrorPanel } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetDynamicPricingStatesQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { dynamicPricingStateSelector, dynamicPricingStatesSelector } from "@/redux/slices/trip/selectors";
import { DynamicPricingState } from "@/redux/slices/trip/types";

interface CustomRatesState {
  states: DynamicPricingState[];
  activeState?: DynamicPricingState;
  stateName: string | null;
  isFetching: boolean;
  isRestricted: boolean;
}

const CustomRatesContext = createContext<CustomRatesState | null>(null);

export const CustomRatesContextProvider = ({ children }: React.PropsWithChildren) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stateName = searchParams.get("stateName");
  const { slug = "" } = useParams();

  const { isFetching, error } = useGetDynamicPricingStatesQuery();
  const states = useAppSelector(dynamicPricingStatesSelector);
  const activeState = useAppSelector(dynamicPricingStateSelector(slug));
  const [isRestricted, setIsRestricted] = useState(false);

  // breadcrumbs setup
  useEffect(() => {
    if (!activeState || activeState.managed) return;

    // only sync if different
    if (stateName !== activeState.name) {
      const next = new URLSearchParams(searchParams);
      next.set("stateName", activeState.name);
      setSearchParams(next, { replace: true });
    }
    // Only react to the values we actually compare
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeState?.name, activeState?.managed, stateName, setSearchParams]);

  // restricted check
  useEffect(() => {
    if (!isFetching && (!activeState || activeState.managed)) setIsRestricted(true);
    else setIsRestricted(false);
  }, [isFetching, activeState]);

  if (error)
    return (
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
        <ErrorPanel messages={getErrorMessages(error)} />
      </div>
    );

  return (
    <CustomRatesContext.Provider value={{ states, activeState, stateName, isFetching, isRestricted }}>
      {children}
    </CustomRatesContext.Provider>
  );
};

export const useCustomRatesContext = () => {
  const context = useContext(CustomRatesContext);
  if (!context) throw new Error("useCustomRatesContext must be used within a CustomRatesContextProvider");

  return context;
};
